import type { AxiosResponse } from 'axios'
import api from './api'
import type {
  CloseFiscalPeriodPayload,
  CloseFiscalPeriodResult,
  ClosingEntryPreview,
  FiscalPeriod,
  FiscalPeriodCreatePayload,
  FiscalPeriodFilters,
  FiscalPeriodListResponse,
  FiscalPeriodPreviewParams,
  FiscalPeriodTrialBalanceParams,
  PeriodEndValidationResult,
  ReopenFiscalPeriodPayload,
  TrialBalanceReport,
  VerifyCarryForwardPayload,
} from '@/types/accounting'

export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T | null
}

type FiscalPeriodResponse<T> = AxiosResponse<ApiResponse<T>>

function normalizeFiscalPeriodList(data: FiscalPeriodListResponse | FiscalPeriod[] | null | undefined): FiscalPeriodListResponse {
  if (Array.isArray(data)) {
    return {
      periods: data,
      totalRows: data.length,
    }
  }

  return {
    periods: data?.periods ?? [],
    totalRows: data?.totalRows ?? data?.periods?.length ?? 0,
  }
}

export const fiscalPeriodsApi = {
  getAll: async (params: FiscalPeriodFilters = {}): Promise<FiscalPeriodResponse<FiscalPeriodListResponse>> => {
    const response = await api.get<ApiResponse<FiscalPeriodListResponse | FiscalPeriod[]>>('/fiscal-periods', { params })
    response.data = {
      ...response.data,
      // Merge, don't replace: rebuilding `data` from only periods/totalRows
      // silently dropped the server's page/totalPages/hasNextPage metadata.
      data: {
        ...(response.data.data as unknown as Record<string, unknown>),
        ...normalizeFiscalPeriodList(response.data.data),
      } as FiscalPeriodListResponse,
    }
    return response as FiscalPeriodResponse<FiscalPeriodListResponse>
  },
  getById: (id: number | string): Promise<FiscalPeriodResponse<FiscalPeriod>> =>
    api.get(`/fiscal-periods/${id}`),
  getCurrent: (): Promise<FiscalPeriodResponse<FiscalPeriod | null>> =>
    api.get('/fiscal-periods/current'),
  create: (data: FiscalPeriodCreatePayload): Promise<FiscalPeriodResponse<FiscalPeriod>> =>
    api.post('/fiscal-periods', data),
  close: (id: number | string, data: CloseFiscalPeriodPayload): Promise<FiscalPeriodResponse<CloseFiscalPeriodResult>> =>
    api.post(`/fiscal-periods/${id}/close`, data),
  reopen: (id: number | string, data: ReopenFiscalPeriodPayload): Promise<FiscalPeriodResponse<FiscalPeriod>> =>
    api.post(`/fiscal-periods/${id}/reopen`, data),
  previewClosing: (id: number | string, params: FiscalPeriodPreviewParams = {}): Promise<FiscalPeriodResponse<ClosingEntryPreview>> =>
    api.get(`/fiscal-periods/${id}/preview-closing`, { params }),
  validationChecklist: (id: number | string): Promise<FiscalPeriodResponse<PeriodEndValidationResult>> =>
    api.get(`/fiscal-periods/${id}/validation-checklist`),
  getTrialBalance: (id: number | string, params: FiscalPeriodTrialBalanceParams = {}): Promise<FiscalPeriodResponse<TrialBalanceReport>> =>
    api.get(`/fiscal-periods/${id}/trial-balance`, { params }),
  getPostClosingTB: (id: number | string, params: FiscalPeriodTrialBalanceParams = {}): Promise<FiscalPeriodResponse<TrialBalanceReport>> =>
    api.get(`/fiscal-periods/${id}/post-closing-trial-balance`, { params: { ...params, format: 'json' } }),
  verifyCarryForward: (data: VerifyCarryForwardPayload): Promise<FiscalPeriodResponse<unknown>> =>
    api.post('/fiscal-periods/verify-carry-forward', data),
  previewCarryForward: (id: number | string): Promise<FiscalPeriodResponse<unknown>> =>
    api.get(`/fiscal-periods/${id}/preview-carry-forward`),
  carryForward: (id: number | string, targetPeriodId?: number | null): Promise<FiscalPeriodResponse<unknown>> =>
    api.post(`/fiscal-periods/${id}/carry-forward`, { target_period_id: targetPeriodId ?? undefined }),
  interimClose: (id: number | string): Promise<FiscalPeriodResponse<FiscalPeriod>> =>
    api.post(`/fiscal-periods/${id}/interim-close`),
  interimStatus: (id: number | string): Promise<FiscalPeriodResponse<unknown>> =>
    api.get(`/fiscal-periods/${id}/interim-status`),
  postClosingPdf: (id: number | string): Promise<AxiosResponse<Blob>> =>
    api.get(`/fiscal-periods/${id}/post-closing-trial-balance`, { responseType: 'blob' }),
}

export function extractFiscalPeriodData<T>(response: FiscalPeriodResponse<T>): T | null {
  return response.data.data
}

import {
  useGetFiscalPeriodsQuery,
  useGetCurrentFiscalPeriodQuery,
  useGetValidationChecklistQuery,
  useLazyGetValidationChecklistQuery,
  useGetClosingChecklistQuery,
  useRunPeriodValidationsMutation,
  useClosePeriodMutation,
  usePreviewCarryForwardMutation,
  useCarryForwardMutation,
  useVerifyCarryForwardMutation,
} from '@/redux/api/fiscalPeriodsApi';

export {
  useGetFiscalPeriodsQuery,
  useGetCurrentFiscalPeriodQuery,
  useGetValidationChecklistQuery,
  useLazyGetValidationChecklistQuery,
  useGetClosingChecklistQuery,
  useRunPeriodValidationsMutation,
  useClosePeriodMutation,
  usePreviewCarryForwardMutation,
  useCarryForwardMutation,
  useVerifyCarryForwardMutation,
};

export const useGetAllPeriodsQuery = useGetFiscalPeriodsQuery;
export const useGetAllFiscalPeriodsQuery = useGetFiscalPeriodsQuery;
export const useGetCurrentPeriodQuery = useGetCurrentFiscalPeriodQuery;

export {
  useGetFinancialKpisQuery as useGetKpisQuery,
  useGetFinancialKpiTrendQuery as useGetTrendQuery,
} from '@/redux/api/financialKpiApi'